import type { useGT } from 'gt-react';
import { as400ColumnEditKey } from '@erp/rpc';
import type { As400ExportColumn } from '@erp/rpc';
import type { As400NumberFormat } from '@erp/rpc';
import type {
  CellViolation,
  CellViolationDetail,
  NumberFormatProblem,
} from '@erp/item-master-rules';
import { formatPricingFactor, isBlocking } from '@erp/item-master-rules';
import type { PendingCellViolation } from './as400GridCellRules';
import {
  describeBlankCellWarning,
  isBlankCellDetail,
} from './as400BlankCellMessages';
import {
  describeWarningCodeViolation,
  isWarningCodeDetail,
} from './as400WarningCodeMessages';

type Gt = ReturnType<typeof useGT>;

/** Names a field the way its column header reads. */
export type ColumnNamer = (field: string) => string;

// A message quotes at most this much of a value, so a large paste can't bury
// the limit and the fix; the cell itself keeps the whole value.
const EXCERPT_LENGTH = 48;

/** The value as a message quotes it: in quotes, abbreviated when long. */
export function quotedExcerpt(value: string | null): string {
  const text = value ?? '';
  const excerpt =
    text.length > EXCERPT_LENGTH
      ? `${text.slice(0, EXCERPT_LENGTH - 1)}…`
      : text;
  return `'${excerpt}'`;
}

/**
 * Names an edit key the way its column header reads, on one line — how a
 * validation message refers to a cell. A bare Y/N flag is named with the
 * column it flags.
 */
export function columnNamer(
  columns: readonly As400ExportColumn[],
): ColumnNamer {
  const headers = new Map<string, string>();
  let previous: string | null = null;
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    const header = oneLine(column.header);
    // A bare flag column ("Y/N", "UPDATE Y/N") is the flag of the column to
    // its left, and says nothing on its own: "SUPL 3 QUOTE Y/N".
    const name =
      BARE_FLAG_HEADER.test(header) && previous
        ? `${previous} ${header}`
        : header;
    if (editKey && !headers.has(editKey)) headers.set(editKey, name);
    previous = header;
  }
  return (editKey) => headers.get(editKey) ?? editKey;
}

/** A flag column's header that doesn't name what it flags. */
const BARE_FLAG_HEADER = /^(UPDATE\s*)?Y\/N$/i;

/** A workbook header on one line (some carry line breaks). */
function oneLine(header: string): string {
  return header.replace(/\s+/g, ' ').trim();
}

/**
 * What is wrong with one cell and how to fix it, naming the column — the text
 * the popup, the cell's tooltip and the error list all show.
 */
export function describeCellViolation(
  gt: Gt,
  violation: CellViolation,
  columnName: ColumnNamer,
): string {
  // The quotes travel in the variable: in the message itself a quote is ICU's
  // escape, and would render the placeholder as literal text.
  const subject: MessageSubject = {
    column: columnName(violation.field),
    value: quotedExcerpt(violation.value),
    text: violation.value ?? '',
    name: columnName,
  };
  if (isBlankCellDetail(violation.detail)) {
    return (
      describeBlankCellWarning(
        gt,
        violation.detail,
        subject.column,
        subject.value,
      ) ?? ''
    );
  }
  if (isWarningCodeDetail(violation.detail)) {
    return (
      describeWarningCodeViolation(
        gt,
        violation.detail,
        subject.column,
        subject.value,
        subject.name,
      ) ?? ''
    );
  }
  return describeDetail(gt, violation.detail, subject);
}

/** What every message names: the column, and the value (quoted, and raw). */
interface MessageSubject {
  column: string;
  value: string;
  text: string;
  /** Names another field a message refers to. */
  name: ColumnNamer;
}

type CatalogCellDetail = Exclude<
  CellViolationDetail,
  {
    rule:
      | 'slash-count'
      | 'whitespace-only'
      | 'not-on-allowlist'
      | 'warning-code-placement';
  }
>;

type LengthOrValuesDetail = Extract<
  CatalogCellDetail,
  { rule: 'max-length' | 'exact-length' | 'number-format' | 'allowed-values' }
>;

type UsageConstraintDetail = Extract<
  CatalogCellDetail,
  { rule: 'not-in-use' | 'max-amount' | 'not-loaded' }
>;

type ItemNumberOrMoveDetail = Extract<
  CatalogCellDetail,
  {
    rule:
      'move-to-description' | 'item-number-format' | 'item-number-collision';
  }
>;

type NamedFieldDetail = Extract<
  CatalogCellDetail,
  {
    rule:
      | 'required-flag'
      | 'no-effect'
      | 'reserved-positions'
      | 'item-prefix'
      | 'item-add-required'
      | 'cannot-clear'
      | 'required'
      | 'upc';
  }
>;

function isLengthOrValues(
  detail: CatalogCellDetail,
): detail is LengthOrValuesDetail {
  return (
    detail.rule === 'max-length' ||
    detail.rule === 'exact-length' ||
    detail.rule === 'number-format' ||
    detail.rule === 'allowed-values'
  );
}

function isUsageConstraint(
  detail: CatalogCellDetail,
): detail is UsageConstraintDetail {
  return (
    detail.rule === 'not-in-use' ||
    detail.rule === 'max-amount' ||
    detail.rule === 'not-loaded'
  );
}

function isItemNumberOrMove(
  detail: CatalogCellDetail,
): detail is ItemNumberOrMoveDetail {
  return (
    detail.rule === 'move-to-description' ||
    detail.rule === 'item-number-format' ||
    detail.rule === 'item-number-collision'
  );
}

function isNamedFieldDetail(
  detail: CatalogCellDetail,
): detail is NamedFieldDetail {
  return (
    detail.rule === 'required-flag' ||
    detail.rule === 'no-effect' ||
    detail.rule === 'reserved-positions' ||
    detail.rule === 'item-prefix' ||
    detail.rule === 'item-add-required' ||
    detail.rule === 'cannot-clear' ||
    detail.rule === 'required' ||
    detail.rule === 'upc'
  );
}

function describeLengthOrValues(
  gt: Gt,
  detail: LengthOrValuesDetail,
  subject: MessageSubject,
): string {
  const { column, value } = subject;
  switch (detail.rule) {
    case 'max-length':
      return gt(
        '{column} {value} is {length} characters; the limit is {limit}. Remove {over, plural, one {# character} other {# characters}} to save it.',
        {
          column,
          value,
          length: detail.length,
          limit: detail.limit,
          over: detail.length - detail.limit,
          context:
            'review grid validation: a typed or pasted value (shown in quotes after the column name) is longer than its AS/400 column allows; says by how many characters',
        },
      );
    case 'exact-length':
      return gt(
        '{column} {value} is {actual, plural, one {# character} other {# characters}}; it must be exactly {length}.',
        {
          column,
          value,
          actual: detail.actual,
          length: detail.length,
          context:
            'review grid validation: a fixed-width AS/400 code (shown in quotes after the column name) is shorter or longer than its exact width',
        },
      );
    case 'number-format':
      return describeNumberFormat(gt, detail.problem, detail.format, subject);
    case 'allowed-values':
      return detail.blankAllowed
        ? gt(
            '{column} {value} is not allowed here. Enter one of {allowed}, or leave it blank.',
            {
              column,
              value,
              allowed: detail.allowed.join(', '),
              context:
                'review grid validation: the value (in quotes) is not one of the codes this AS/400 column accepts; lists them, and blank is also fine',
            },
          )
        : gt('{column} {value} is not allowed here. Enter one of {allowed}.', {
            column,
            value,
            allowed: detail.allowed.join(', '),
            context:
              'review grid validation: the value (in quotes, may be empty) is not one of the codes this AS/400 column accepts; lists them',
          });
  }
}

function describeUsageConstraint(
  gt: Gt,
  detail: UsageConstraintDetail,
  subject: MessageSubject,
): string {
  const { column, value } = subject;
  switch (detail.rule) {
    case 'not-in-use':
      return gt(
        '{column} is not in use yet. Check with {person} that it should be loaded before uploading {value}.',
        {
          column,
          value,
          person: detail.confirmWith,
          context:
            'review grid validation warning: nobody loads this AS/400 field today; names the person who decides when it starts being used',
        },
      );
    case 'max-amount':
      return gt(
        '{column} {value} must be at most {limit} on every RD and selling division together.',
        {
          column,
          value:
            detail.siblingValue != null
              ? quotedExcerpt(detail.siblingValue)
              : value,
          limit: detail.limit,
          context:
            'review grid validation: a cost is above the shared Buyer Cost cap; RD and selling must stay at or under it together so a partial upload cannot desync',
        },
      );
    case 'not-loaded':
      return gt(
        "{column} is calculated from Buyer's Cost. Check a typed value before uploading it.",
        {
          column,
          context:
            "review grid validation warning: Landed Cost is calculated from Buyer's Cost; a typed value can still be saved, so check it before uploading",
        },
      );
  }
}

function describeItemNumberOrMove(
  gt: Gt,
  detail: ItemNumberOrMoveDetail,
  subject: MessageSubject,
): string {
  switch (detail.rule) {
    case 'move-to-description':
      return describeMoveToDescription(gt, detail, subject);
    case 'item-number-format':
      return describeItemNumberFormat(gt, detail.problem, subject);
    case 'item-number-collision':
      return describeItemNumberCollision(gt, detail.suggestion, subject);
  }
}

function describeNamedFieldDetail(
  gt: Gt,
  detail: NamedFieldDetail,
  subject: MessageSubject,
): string {
  switch (detail.rule) {
    case 'required-flag':
    case 'no-effect':
    case 'reserved-positions':
    case 'item-prefix':
      return describeFieldInterplay(gt, detail, subject);
    case 'item-add-required':
      return describeItemAddRequired(gt, detail, subject);
    case 'cannot-clear':
    case 'required':
      return describeRequiredField(gt, detail, subject);
    case 'upc':
      return describeUpc(gt, subject);
  }
}

function describeDetail(
  gt: Gt,
  detail: CatalogCellDetail,
  subject: MessageSubject,
): string {
  if (isLengthOrValues(detail)) {
    return describeLengthOrValues(gt, detail, subject);
  }
  if (isUsageConstraint(detail)) {
    return describeUsageConstraint(gt, detail, subject);
  }
  if (isItemNumberOrMove(detail)) {
    return describeItemNumberOrMove(gt, detail, subject);
  }
  if (isNamedFieldDetail(detail)) {
    return describeNamedFieldDetail(gt, detail, subject);
  }
  return describeFactorOrRelated(gt, detail, subject);
}

function describeUpc(gt: Gt, { column, value }: MessageSubject): string {
  return gt(
    '{column} {value} has an invalid UPC check digit; AS/400 may reject it.',
    {
      column,
      value,
      context:
        'review grid validation: MFTR ID plus Item UPC failed the UPC-A mod-10 check digit; Save is still allowed',
    },
  );
}

/** Details that read or name another field than the cell's own. */
type InterplayDetail = Extract<
  CellViolationDetail,
  { rule: 'required-flag' | 'no-effect' | 'reserved-positions' | 'item-prefix' }
>;

function describeFieldInterplay(
  gt: Gt,
  detail: InterplayDetail,
  { column, value, name }: MessageSubject,
): string {
  switch (detail.rule) {
    case 'required-flag':
      return gt(
        '{column} must be {required} when {cause} is {causeValue}; otherwise the AS/400 ignores the change.',
        {
          column,
          required: `'${detail.required}'`,
          cause: name(detail.cause.field),
          causeValue: `'${detail.cause.value}'`,
          context:
            'review grid validation: a Y/N flag column must be set (to the quoted value) for the AS/400 to act on the value typed in another column',
        },
      );
    case 'no-effect':
      return gt(
        '{column} {value} does nothing on the AS/400. Enter {instead} to make a change, or leave it blank.',
        {
          column,
          value,
          instead: detail.instead.join(', '),
          context:
            'review grid validation warning: the value is accepted but the AS/400 ignores it; lists the values that do something',
        },
      );
    case 'reserved-positions':
      return gt(
        '{column} {value} is {length} characters; only the first {limit} are entered here. The AS/400 sets position {next} from {owner}, so remove the rest.',
        {
          column,
          value,
          length: detail.length,
          limit: detail.limit,
          next: detail.limit + 1,
          owner: name(detail.owner),
          context:
            'review grid validation: a position-based AS/400 field whose last character is written from another column (named at the end)',
        },
      );
    case 'item-prefix':
      return gt(
        '{column} {value} is usually set only on items whose {item} starts with {prefix}. Check that this item should have it.',
        {
          column,
          value,
          item: name(detail.itemField),
          prefix: detail.prefix,
          context:
            'review grid validation warning: a flag value (in quotes) is expected only on items whose number starts with the given prefix; saving is still allowed',
        },
      );
  }
}

function describeItemAddRequired(
  gt: Gt,
  detail: Extract<CellViolationDetail, { rule: 'item-add-required' }>,
  { column, value }: MessageSubject,
): string {
  return detail.missingLanguages.length === 0
    ? gt('{column} {value} needs an Item Add record for this part number.', {
        column,
        value,
        context:
          'review grid validation: Hazmat or Shelf Life is Y and this part has no Item Add record',
      })
    : gt('{column} {value} needs Item Add records in {languages}.', {
        column,
        value,
        languages: detail.missingLanguages.join(', '),
        context:
          'review grid validation: Shelf Life is Y and Item Add is missing English or French',
      });
}

function describeRequiredField(
  gt: Gt,
  detail: Extract<CellViolationDetail, { rule: 'cannot-clear' | 'required' }>,
  { column, value }: MessageSubject,
): string {
  if (detail.rule === 'cannot-clear') {
    return gt(
      '{column} {value} cannot be used to clear this field. Enter a real value.',
      {
        column,
        value,
        context:
          'review grid validation: a slash-only value would clear a field that must stay filled on every row',
      },
    );
  }
  return gt('{column} is required.', {
    column,
    context:
      'review grid validation: this field must have a value on a new row; blank is allowed on a change because it means do not change',
  });
}

/** The kinds a message names in terms of other fields or a supplier section. */
type RelatedFieldDetail = Extract<
  CellViolationDetail,
  {
    rule:
      | 'ship-direct'
      | 'min-quantity-required'
      | 'change-flag'
      | 'paired-edit'
      | 'needs-field'
      | 'should-be-zero'
      | 'zero-dimension';
  }
>;

type FactorDetail = Extract<
  CellViolationDetail,
  {
    rule:
      | 'pricing-factor'
      | 'purchasing-factor-required'
      | 'purchasing-factor-must-be-one';
  }
>;

function describeFactorDetail(
  gt: Gt,
  detail: FactorDetail,
  { column, value }: MessageSubject,
): string {
  if (detail.rule === 'purchasing-factor-required') {
    return gt(
      '{column} is required on a new item while Supplier {section} has data.',
      {
        column,
        section: detail.section,
        context:
          "review grid validation: a new item's supplier section needs its purchasing UOM factor",
      },
    );
  }
  if (detail.rule === 'purchasing-factor-must-be-one') {
    return gt(
      '{column} {value} must be 1 when UOM matches STOCK UOM ({uom}).',
      {
        column,
        value,
        uom: detail.stockUom,
        context:
          'review grid validation: UOM FACTOR must be 1 when the supplier section UOM matches the item STOCK UOM',
      },
    );
  }
  return gt('{column} {value} must be {expected} when Sell UOM is {uom}.', {
    column,
    value,
    expected: formatPricingFactor(detail.expected),
    uom: detail.sellUom,
    context:
      'review grid validation: Pricing Factor must match the mapped factor for the Sell UOM code',
  });
}

function isFactorDetail(
  detail: FactorDetail | RelatedFieldDetail,
): detail is FactorDetail {
  return (
    detail.rule === 'pricing-factor' ||
    detail.rule === 'purchasing-factor-required' ||
    detail.rule === 'purchasing-factor-must-be-one'
  );
}

function describeFactorOrRelated(
  gt: Gt,
  detail: FactorDetail | RelatedFieldDetail,
  subject: MessageSubject,
): string {
  if (isFactorDetail(detail)) return describeFactorDetail(gt, detail, subject);
  return describeRelatedFieldDetail(gt, detail, subject);
}

function describeRelatedFieldDetail(
  gt: Gt,
  detail: RelatedFieldDetail,
  { column, value, name }: MessageSubject,
): string {
  switch (detail.rule) {
    case 'ship-direct':
      return gt(
        '{column} must be Y while Supplier {section} has data, or the AS/400 drops the section and everything entered in it.',
        {
          column,
          section: detail.section,
          context:
            "review grid validation: a supplier section's Ship Direct (SD) flag must be Y whenever the section is filled in",
        },
      );
    case 'min-quantity-required':
      return gt(
        '{column} is required on a new item while Supplier {section} has data.',
        {
          column,
          section: detail.section,
          context:
            "review grid validation: a new item's supplier section needs its minimum order quantity",
        },
      );
    case 'change-flag':
      return detail.cleared
        ? gt('{column} must be Y to clear {target}.', {
            column,
            target: name(detail.field),
            context:
              'review grid validation: an AS/400 update flag (Y/N) must be Y for a cleared value (the target column) to be cleared',
          })
        : gt('{column} must be Y for the change to {target} to load.', {
            column,
            target: name(detail.field),
            context:
              'review grid validation: an AS/400 update flag (Y/N) must be Y for the changed value in the target column to be taken',
          });
    case 'paired-edit':
      return gt('{cause} changed. Update {column} to match.', {
        column,
        cause: name(detail.field),
        context:
          'review grid validation warning: a column (e.g. a product warning code) was changed but the column that goes with it (e.g. its product remark) was not',
      });
    case 'needs-field':
      return gt('{column} {value} needs {target} filled in to load.', {
        column,
        value,
        target: name(detail.field),
        context:
          'review grid validation: the value (in quotes) cannot be loaded without another column, e.g. a quote number without its supplier number',
      });
    case 'should-be-zero':
      return gt(
        '{column} has been unused for years. Leave it at 0 to avoid upload errors.',
        {
          column,
          context:
            'review grid validation warning: the supplier minimum dollar amount is obsolete; anything but 0 risks the upload',
        },
      );
    case 'zero-dimension':
      return gt(
        '{column} is 0, which can fail the upload. Enter the real value, or leave it blank to clear it.',
        {
          column,
          context:
            'review grid validation warning: a product dimension (height, width, length, weight, density) of 0 can fail the AS/400 upload',
        },
      );
  }
}

type ItemNumberFormatProblem = Extract<
  CellViolationDetail,
  { rule: 'item-number-format' }
>['problem'];

function describeItemNumberFormat(
  gt: Gt,
  problem: ItemNumberFormatProblem,
  { column, value }: MessageSubject,
): string {
  return problem === 'truncation-marker'
    ? gt(
        '{column} {value} can use $ only as the truncation marker, followed by the number of characters removed (for example $12).',
        {
          column,
          value,
          context:
            'review grid validation: $ in an AS/400 item number is only the truncation marker, then the count of removed characters',
        },
      )
    : gt('{column} {value} can only use A–Z, 0–9, period, and slash.', {
        column,
        value,
        context:
          'review grid validation: an AS/400 item number has a character other than letters, digits, period, or slash',
      });
}

function describeItemNumberCollision(
  gt: Gt,
  suggestion: string | null,
  { column, value }: MessageSubject,
): string {
  return suggestion === null
    ? gt(
        '{column} {value} already exists. Remove one more character to make it unique.',
        {
          column,
          value,
          context:
            'review grid validation warning: a new AS/400 item number collides with one already in the catalog',
        },
      )
    : gt(
        '{column} {value} already exists. Remove one more character and use {suggestion}.',
        {
          column,
          value,
          suggestion: `'${suggestion}'`,
          context:
            'review grid validation warning: a new AS/400 item number collides with one already in the catalog; ends with the suggested $N+1 number to copy',
        },
      );
}

type MoveToDescription = Extract<
  CellViolationDetail,
  { rule: 'move-to-description' }
>;

/**
 * A supplier part number too long for its column (GEN-2766): it is left blank
 * and the part number goes in the English description — and, past that, on
 * the Item Add lines from 10 down. Spells out each piece in full, to copy.
 */
function describeMoveToDescription(
  gt: Gt,
  detail: MoveToDescription,
  { column, value }: MessageSubject,
): string {
  const intro =
    detail.clearWith === null
      ? gt(
          '{column} {value} is {length} characters; the limit is {limit}. Leave {column} blank and put the part number in the ENGLISH DESCRIPTION on the ITEM sheet: {description}',
          {
            column,
            value,
            length: detail.length,
            limit: detail.limit,
            description: `'${detail.description}'`,
            context:
              'review grid validation: a supplier part number (in quotes) is too long for its AS/400 column, so the rule is to leave it blank and type it into the item English description instead; ends with the exact text to type there, in quotes',
          },
        )
      : gt(
          '{column} {value} is {length} characters; the limit is {limit}. Clear {column} out (right-click, Clear out, or type {marker}) and put the part number in the ENGLISH DESCRIPTION on the ITEM sheet: {description}',
          {
            column,
            value,
            length: detail.length,
            limit: detail.limit,
            marker: `'${detail.clearWith}'`,
            description: `'${detail.description}'`,
            context:
              "review grid validation, on an existing item: a supplier part number (in quotes) is too long for its AS/400 column; a blank cell would leave the old value, so the author clears the cell out (the grid's right-click Clear out, or the quoted slash marker) and types the part number into the item English description instead; ends with the exact text to type there, in quotes",
          },
        );
  if (detail.additionalLines.length === 0) return intro;
  const lines = detail.additionalLines
    .map(({ line, text }) =>
      gt('line {line}: {text}', {
        line,
        text: `'${text}'`,
        context:
          'review grid validation: one Item Add (additional description) line number and the exact text (in quotes) to type on it',
      }),
    )
    .join('; ');
  const continued = gt(
    'Continue the rest on the ITEM ADD sheet under language {language}, {limit} characters a line from line 10 down: {lines}.',
    {
      language: detail.language,
      limit: detail.lineLimit,
      lines,
      context:
        'review grid validation: the part number is longer than the description holds, so the rest goes on the Item Add (additional description) lines under the given language code (E is English), starting at line 10 and working down; lists each line and its text',
    },
  );
  const leftOver =
    detail.leftOver > 0
      ? gt(
          '{count} characters do not fit even on all ten lines; shorten the part number.',
          {
            count: detail.leftOver,
            context:
              'review grid validation: the part number is too long for the description plus all ten Item Add lines',
          },
        )
      : null;
  return [intro, continued, leftOver].filter(Boolean).join(' ');
}

/** A mask the way the workbook writes it: `99999.9999`. */
function numberMask(format: As400NumberFormat): string {
  const whole = '9'.repeat(format.integerDigits ?? 1);
  const fraction = '9'.repeat(format.fractionDigits);
  return fraction ? `${whole}.${fraction}` : whole;
}

/** The digits of a numeric text before and after its point. */
function digitCounts(text: string): { whole: number; fraction: number } {
  const [whole = '', fraction = ''] = text.split('.');
  return { whole: whole.length, fraction: fraction.length };
}

// Problems with what the value is made of, rather than how many digits it has.
type ShapeProblem = Extract<
  NumberFormatProblem,
  'not-a-number' | 'whole-number'
>;
type DigitCountProblem = Exclude<NumberFormatProblem, ShapeProblem>;

function isShapeProblem(problem: NumberFormatProblem): problem is ShapeProblem {
  return problem === 'not-a-number' || problem === 'whole-number';
}

function describeNumberFormat(
  gt: Gt,
  problem: NumberFormatProblem,
  format: As400NumberFormat,
  subject: MessageSubject,
): string {
  return isShapeProblem(problem)
    ? describeNumberShape(gt, problem, format, subject)
    : describeDigitCount(gt, problem, format, subject);
}

/** A value made of the wrong things: not a number, a point, a non-digit. */
function describeNumberShape(
  gt: Gt,
  problem: ShapeProblem,
  format: As400NumberFormat,
  { column, value }: MessageSubject,
): string {
  if (problem === 'not-a-number') {
    return format.fractionDigits === 0
      ? gt('{column} {value} is not a whole number. Enter digits only.', {
          column,
          value,
          context:
            'review grid validation: a whole-number AS/400 column got text that is not digits',
        })
      : gt(
          '{column} {value} is not a number. Enter digits, with at most one decimal point.',
          {
            column,
            value,
            context:
              'review grid validation: a numeric AS/400 column got text that is not a number',
          },
        );
  }
  return gt(
    '{column} takes whole numbers only. Remove the decimal point from {value}.',
    {
      column,
      value,
      context:
        'review grid validation: a decimal was typed in an AS/400 column whose format has no decimal places',
    },
  );
}

/** A number with more digits than the format has room for. */
function describeDigitCount(
  gt: Gt,
  problem: DigitCountProblem,
  format: As400NumberFormat,
  { column, value, text }: MessageSubject,
): string {
  const mask = numberMask(format);
  const digits = digitCounts(text);
  switch (problem) {
    case 'integer-digits':
      return gt(
        '{column} {value} has {count} digits before the decimal point; the format {mask} allows {limit}.',
        {
          column,
          value,
          count: digits.whole,
          mask,
          limit: format.integerDigits ?? 0,
          context:
            'review grid validation: a number has more digits before the point than the AS/400 number format (shown like 9999.999) allows',
        },
      );
    case 'fraction-digits':
      return gt(
        '{column} {value} has {count} digits after the decimal point; the format {mask} allows {limit}.',
        {
          column,
          value,
          count: digits.fraction,
          mask,
          limit: format.fractionDigits,
          context:
            'review grid validation: a number has more decimal places than the AS/400 number format (shown like 9999.999) allows',
        },
      );
  }
}

/** A popup's title and message. */
export interface ViolationNotice {
  title: string;
  message: string;
}

/**
 * The popup raised the moment a committed value breaks a rule — when the
 * author leaves the cell, or a paste lands. Names the first problem and says
 * how many cells are affected; each one is outlined on the grid.
 */
export function leftCellNotice(
  gt: Gt,
  located: readonly PendingCellViolation[],
  columnName: ColumnNamer,
): ViolationNotice {
  const errors = located.filter((entry) => isBlocking(entry.violation));
  // An error is named first: it is what stops Save.
  const first = (errors[0] ?? located[0])?.violation;
  return {
    title:
      errors.length > 0
        ? fixCellsTitle(gt, cellCount(errors))
        : checkCellsTitle(gt, cellCount(located)),
    message: first ? describeCellViolation(gt, first, columnName) : '',
  };
}

/**
 * How many cells the violations are on: one cell can break several rules
 * (`123` in a two-character code is both too long and not exactly two).
 */
function cellCount(located: readonly PendingCellViolation[]): number {
  return new Set(
    located.map(({ rowKey, violation }) => `${rowKey}\n${violation.field}`),
  ).size;
}

function fixCellsTitle(gt: Gt, count: number): string {
  return count === 1
    ? gt('Fix this cell before saving', {
        context:
          'review grid validation popup title: the cell just left breaks a rule',
      })
    : gt('Fix {count} cells before saving', {
        count,
        context:
          'review grid validation popup title: several cells just pasted or edited break a rule',
      });
}

function checkCellsTitle(gt: Gt, count: number): string {
  return count === 1
    ? gt('Check this cell', {
        context:
          'review grid validation popup title: the cell just left draws a warning; saving is still allowed',
      })
    : gt('Check {count} cells', {
        count,
        context:
          'review grid validation popup title: several cells just pasted or edited draw a warning; saving is still allowed',
      });
}

/**
 * The popup raised when Save is refused: nothing was staged, and why. The
 * offending cells stay pending and outlined, so fixing them and saving again
 * goes through.
 */
export function itemNumberLookupNotice(
  gt: Gt,
  failed: boolean,
): ViolationNotice {
  return {
    title: gt('Nothing saved: still checking item numbers', {
      context:
        'review grid save refused because the catalog item-number lookup has not finished or failed',
    }),
    message: failed
      ? gt('The catalog could not be checked. Try saving again.', {
          context:
            'review grid save refused because the catalog item-number lookup failed',
        })
      : gt('Wait until the catalog check finishes, then save again.', {
          context:
            'review grid save refused because the catalog item-number lookup is still running',
        }),
  };
}

export function saveBlockedNotice(
  gt: Gt,
  located: readonly PendingCellViolation[],
  columnName: ColumnNamer,
): ViolationNotice {
  const first = located[0]?.violation;
  const count = cellCount(located);
  const title =
    count === 1
      ? gt('Nothing saved: 1 cell needs fixing', {
          context:
            'review grid validation popup title: Save was refused because one unsaved cell breaks a rule',
        })
      : gt('Nothing saved: {count} cells need fixing', {
          count,
          context:
            'review grid validation popup title: Save was refused because several unsaved cells break a rule',
        });
  return {
    title,
    message: first ? describeCellViolation(gt, first, columnName) : '',
  };
}
